import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, PhoneOutgoing } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P } from '../api';
import { Page, Section, Field, Select, Table, Td, Pill, Empty, Loading, ErrorNote, DISPOSITIONS, useLoad, useTS } from '../ui';
import { STAGE_LABELS } from './TSLeads';

export default function TSCampaigns() {
  const session = useTS();
  const list = useLoad(() => ts.campaigns());
  const [creating, setCreating] = useState(false);
  const [working, setWorking] = useState(null);
  const manage = session.can(P.CAMPAIGNS);

  const setStatus = async (id, status) => {
    try { await ts.campaignStatus(id, status); list.reload(); } catch (e) { toast.error(e.message); }
  };

  if (working) return <WorkCampaign campaign={working} onExit={() => { setWorking(null); list.reload(); }} />;
  return (
    <Page title="Campaigns" subtitle="Call lists worked by your team, with results tracked per lead."
      actions={manage && <Button size="sm" onClick={() => setCreating(true)} data-testid="ts-new-campaign"><Plus className="w-4 h-4 mr-1" /> New campaign</Button>}>
      {creating && <NewCampaign onClose={() => setCreating(false)} onSaved={() => { setCreating(false); list.reload(); }} />}
      <Section>
        {list.loading ? <Loading /> : (list.data || []).length === 0 ? <Empty>No campaigns yet.</Empty> : (
          <Table head={['Campaign', 'Status', 'Leads', 'Progress', 'Results', '']}>
            {list.data.map(c => (
              <tr key={c.id}>
                <Td><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.members.map(m => m.name).join(', ') || 'Whole team'}</div></Td>
                <Td><Pill tone={c.status === 'active' ? 'green' : 'gray'}>{c.status}</Pill></Td>
                <Td>{c.size}{c.skipped_suppressed ? <div className="text-xs text-muted-foreground">{c.skipped_suppressed} skipped (do-not-call)</div> : null}</Td>
                <Td className="text-xs">{Object.entries(c.progress).map(([k, v]) => `${k}: ${v}`).join(' · ')}</Td>
                <Td className="text-xs">{Object.entries(c.results).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</Td>
                <Td className="text-right whitespace-nowrap">
                  {c.status === 'active' && session.can(P.CALL) && <Button size="sm" onClick={() => setWorking(c)}><PhoneOutgoing className="w-4 h-4 mr-1" /> Start calling</Button>}
                  {manage && c.status !== 'active' && c.status !== 'completed' && <Button size="sm" variant="ghost" onClick={() => setStatus(c.id, 'active')}>Start</Button>}
                  {manage && c.status === 'active' && <Button size="sm" variant="ghost" onClick={() => setStatus(c.id, 'paused')}>Pause</Button>}
                  {manage && c.status !== 'completed' && <Button size="sm" variant="ghost" onClick={() => setStatus(c.id, 'completed')}>Complete</Button>}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </Page>
  );
}

function NewCampaign({ onClose, onSaved }) {
  const staff = useLoad(() => ts.staff());
  const [form, setForm] = useState({ name: '', stage: '', tag: '', script: '', description: '', member_ids: [], daily_start: '09:00', daily_end: '18:00' });
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });
  const toggle = (id) => setForm(f => ({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter(x => x !== id) : [...f.member_ids, id] }));
  const save = async (e) => {
    e.preventDefault();
    try { const c = await ts.createCampaign(form); toast.success(`Campaign created with ${c.size} leads`); onSaved(); } catch (err) { setError(err.message); }
  };
  return (
    <Section title="New campaign">
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ErrorNote error={error} />
        <Field label="Name"><Input required value={form.name} onChange={set('name')} data-testid="ts-campaign-name" /></Field>
        <Field label="Leads in stage"><Select value={form.stage} onChange={set('stage')} options={[['', 'Any stage'], ...Object.entries(STAGE_LABELS)]} /></Field>
        <Field label="Leads with tag"><Input value={form.tag} onChange={set('tag')} /></Field>
        <Field label="Calling hours (company time)">
          <div className="flex gap-2"><Input type="time" value={form.daily_start} onChange={set('daily_start')} /><Input type="time" value={form.daily_end} onChange={set('daily_end')} /></div>
        </Field>
        <Field label="Callers" hint="Leads are split evenly. Leave empty for the whole team.">
          <div className="flex flex-wrap gap-2">
            {(staff.data || []).filter(m => m.status === 'active').map(m => (
              <label key={m.id} className="text-xs flex items-center gap-1"><input type="checkbox" checked={form.member_ids.includes(m.id)} onChange={() => toggle(m.id)} /> {m.full_name}</label>
            ))}
          </div>
        </Field>
        <Field label="Call script"><Textarea rows={4} value={form.script} onChange={set('script')} /></Field>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid="ts-campaign-save">Create</Button>
        </div>
      </form>
    </Section>
  );
}

function WorkCampaign({ campaign, onExit }) {
  const { data, loading, reload } = useLoad(() => ts.campaignNext(campaign.id), [campaign.id]);
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const call = async () => {
    try { const r = await ts.placeCall({ to: data.lead.phone, lead_id: data.lead.id, campaign_id: campaign.id }); toast.success(r.simulated ? 'Test mode: call simulated' : 'Your phone is ringing'); } catch (e) { toast.error(e.message); }
  };
  const result = async (disposition) => {
    try {
      await ts.campaignResult(campaign.id, { lead_id: data.lead.id, disposition, notes, callback_at: callbackAt ? new Date(callbackAt).toISOString() : undefined });
      setNotes(''); setCallbackAt(''); reload();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Page title={campaign.name} actions={<Button size="sm" variant="outline" onClick={onExit}>Back to campaigns</Button>}>
      {loading ? <Loading /> : !data.lead ? <Section><Empty>{data.message}</Empty></Section> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title={data.lead.name}>
            <div className="text-sm">{data.lead.phone} · {data.lead.company || ''}</div>
            <div className="text-xs text-muted-foreground">Attempts so far: {data.entry.attempts}</div>
            <Button onClick={call} data-testid="ts-campaign-call"><PhoneOutgoing className="w-4 h-4 mr-1" /> Call</Button>
            <Field label="Notes"><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} /></Field>
            <Field label="Call back at (optional)"><Input type="datetime-local" value={callbackAt} onChange={e => setCallbackAt(e.target.value)} /></Field>
            <div className="flex flex-wrap gap-1">
              {DISPOSITIONS.map(([k, l]) => <Button key={k} size="sm" variant="outline" onClick={() => result(k)} data-testid={`ts-result-${k}`}>{l}</Button>)}
            </div>
          </Section>
          <Section title="Script">
            <pre className="whitespace-pre-wrap text-sm font-sans">{data.script || 'No script for this campaign.'}</pre>
          </Section>
        </div>
      )}
    </Page>
  );
}
